/**
 * Iter-27.36 — Mode Présentation Premium.
 *
 * Affiche une leçon `rich_content` en slide-by-slide animé plein écran,
 * façon Coursera/Reels éducatif. C'est le mode par défaut pour les leçons
 * écrites (sans vidéo) — beaucoup plus engageant qu'un long scroll de texte.
 *
 * Chaque "section" du rich_content devient une slide. Plus l'intro (1ère)
 * et la punchline (dernière) si elles existent.
 *
 * Anti-farming : propage à Module.jsx la liste des slides vues (Set<idx>).
 * Le bouton "Marquer comme vu" se débloque quand TOUTES les slides ont
 * été vues au moins 3 secondes chacune (en plus du gate serveur min_seconds).
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ChevronLeft,
    ChevronRight,
    Volume2,
    Pause,
    Loader2,
    BookOpen,
    Sparkles,
    Quote as QuoteIcon,
    Target,
    AlertTriangle,
    CheckCircle2,
    XCircle,
    Award,
    Lightbulb,
} from "lucide-react";
import api, { API_BASE } from "../lib/api";
import audioManager from "../lib/audioManager";

// ─── Inline formatting (subset of Markdown) ──────────────────────────────────
function applyInline(text = "") {
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\*\*([^*]+)\*\*/g, "<strong class='text-white font-extrabold'>$1</strong>")
        .replace(/\*([^*]+)\*/g, "<em class='text-shariti-orange-light not-italic font-bold'>$1</em>")
        .replace(
            /`([^`]+)`/g,
            "<code class='bg-white/8 px-1.5 py-0.5 rounded text-shariti-yellow font-mono text-[0.9em]'>$1</code>",
        );
}

// Iter-27.80 — Conversion d'un nombre entier en mot français (1-20+).
// Utilisé pour préfixer les puces des listes numérotées dans le texte TTS,
// afin que le formateur dise « Un, …, Deux, …, Trois, … » comme à l'écran.
const FR_ORDINALS = [
    "zéro", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf",
    "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize",
    "dix-sept", "dix-huit", "dix-neuf", "vingt",
];
function frenchOrdinal(n) {
    if (n >= 0 && n < FR_ORDINALS.length) return FR_ORDINALS[n];
    return String(n);
}

// Iter-27.80 — Nettoyage premium d'un texte avant envoi à ElevenLabs.
// Objectif : la voix du formateur doit sonner naturelle, avec respirations,
// asides sur les parenthèses, et zéro symbole parasite (`**`, `—`, etc.).
function humanizeForSpeech(text = "") {
    if (!text) return "";
    return String(text)
        // 1) Markdown : strip bold/italic/code marks (les *, **, ` ne se lisent pas)
        .replace(/\*\*/g, "")
        .replace(/\*/g, "")
        .replace(/`/g, "")
        // 2) Em/en dash : convertis en virgule + espace (pause naturelle)
        .replace(/\s*[—–]\s*/g, ", ")
        // 3) Parenthèses : transformés en pauses (aside)
        //    « Énergie haute dès la 1ère seconde (sourire, regard) » →
        //    « Énergie haute dès la 1ère seconde, sourire, regard. »
        .replace(/\s*\(\s*/g, ", ")
        .replace(/\s*\)\s*/g, ". ")
        // 4) "1ère" → "première" pour éviter "un-è-r-e" hasardeux
        .replace(/\b1ère\b/gi, "première")
        .replace(/\b1er\b/gi, "premier")
        // 5) Nettoyage final : doubles virgules / points consécutifs, espaces
        .replace(/([,.])\s*([,.])/g, "$1")
        .replace(/\s{2,}/g, " ")
        .replace(/\s+([,.;:!?])/g, "$1")
        .trim();
}

// ─── Helper: build the list of slides from rich_content ──────────────────────
function buildSlides(rich_content) {
    if (!rich_content) return [];
    const slides = [];
    if (rich_content.intro) {
        slides.push({ kind: "intro", body: rich_content.intro });
    }
    for (const s of rich_content.sections || []) {
        slides.push(s);
    }
    if (rich_content.punchline) {
        slides.push({ kind: "punchline", body: rich_content.punchline });
    }
    return slides;
}

// ─── Slide layouts ───────────────────────────────────────────────────────────

function IntroSlide({ body }) {
    return (
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
            <div className="w-14 h-14 rounded-3xl bg-shariti-orange/15 flex items-center justify-center mb-5">
                <BookOpen className="w-7 h-7 text-shariti-orange-light" />
            </div>
            <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-3">
                Pour commencer
            </p>
            <p
                className="text-xl sm:text-2xl text-white/90 font-light leading-relaxed"
                dangerouslySetInnerHTML={{ __html: applyInline(body) }}
            />
        </div>
    );
}

function PunchlineSlide({ body }) {
    return (
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
            <div className="w-14 h-14 rounded-3xl bg-shariti-orange flex items-center justify-center mb-5 orange-glow">
                <Sparkles className="w-7 h-7 text-white" />
            </div>
            <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-3">
                À retenir
            </p>
            <p
                className="text-xl sm:text-2xl text-white font-bold leading-relaxed"
                dangerouslySetInnerHTML={{ __html: applyInline(body) }}
            />
        </div>
    );
}

function TextSlide({ title, body }) {
    return (
        <div className="max-w-3xl mx-auto">
            {title && (
                <>
                    <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-2">
                        Notion clé
                    </p>
                    <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-5 leading-tight">
                        {title}
                    </h3>
                </>
            )}
            <p
                className="text-base sm:text-lg text-white/80 font-light leading-relaxed whitespace-pre-line"
                dangerouslySetInnerHTML={{ __html: applyInline(body) }}
            />
        </div>
    );
}

function StatSlide({ label, value, context }) {
    return (
        <div className="max-w-3xl mx-auto text-center">
            <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-3">
                {label || "Donnée clé"}
            </p>
            <p className="text-5xl sm:text-7xl font-extrabold text-shariti-orange tracking-tight mb-5 leading-none">
                {value}
            </p>
            {context && (
                <p
                    className="text-base sm:text-lg text-white/75 font-light leading-relaxed max-w-2xl mx-auto"
                    dangerouslySetInnerHTML={{ __html: applyInline(context) }}
                />
            )}
        </div>
    );
}

function StepSlide({ number, title, body, example, total }) {
    return (
        <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-4 mb-5">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-shariti-orange flex items-center justify-center font-extrabold text-white text-xl sm:text-2xl shrink-0 orange-glow">
                    {number}
                </div>
                <div>
                    <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light">
                        Étape {number}{total ? ` / ${total}` : ""}
                    </p>
                    <h3 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight">
                        {title}
                    </h3>
                </div>
            </div>
            <p
                className="text-base sm:text-lg text-white/80 font-light leading-relaxed mb-4 whitespace-pre-line"
                dangerouslySetInnerHTML={{ __html: applyInline(body) }}
            />
            {example && (
                <div className="rounded-2xl bg-shariti-yellow/8 border border-shariti-yellow/25 p-4">
                    <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-yellow mb-1.5">
                        Exemple terrain
                    </p>
                    <p className="text-sm sm:text-base text-white/85 italic leading-relaxed">
                        {example}
                    </p>
                </div>
            )}
        </div>
    );
}

function DoDontSlide({ doItems, dontItems }) {
    return (
        <div className="max-w-3xl mx-auto">
            <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-3 text-center">
                À faire / À éviter
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
                <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/8 p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-emerald-400">
                            À faire
                        </p>
                    </div>
                    <ul className="space-y-2">
                        {(doItems || []).map((it, i) => (
                            <li key={i} className="text-sm text-white/85 font-light leading-relaxed flex gap-2">
                                <span className="text-emerald-400 shrink-0">✓</span>
                                <span dangerouslySetInnerHTML={{ __html: applyInline(it) }} />
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="rounded-2xl border border-red-500/25 bg-red-500/8 p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <XCircle className="w-4 h-4 text-red-400" />
                        <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-red-400">
                            À éviter
                        </p>
                    </div>
                    <ul className="space-y-2">
                        {(dontItems || []).map((it, i) => (
                            <li key={i} className="text-sm text-white/85 font-light leading-relaxed flex gap-2">
                                <span className="text-red-400 shrink-0">✕</span>
                                <span dangerouslySetInnerHTML={{ __html: applyInline(it) }} />
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

function WarningSlide({ title, body, items, rule }) {
    return (
        <div className="max-w-3xl mx-auto">
            <div className="rounded-3xl border border-red-500/30 bg-red-500/8 p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-3">
                    <AlertTriangle className="w-6 h-6 text-red-400 shrink-0" />
                    <h3 className="text-lg sm:text-xl font-extrabold text-white tracking-tight">
                        {title}
                    </h3>
                </div>
                {body && (
                    <p
                        className="text-sm sm:text-base text-white/80 font-light leading-relaxed mb-3"
                        dangerouslySetInnerHTML={{ __html: applyInline(body) }}
                    />
                )}
                {items && items.length > 0 && (
                    <ul className="space-y-2 mb-3">
                        {items.map((it, i) => (
                            <li key={i} className="text-sm text-white/80 font-light leading-relaxed flex gap-2">
                                <span className="text-red-400 shrink-0">•</span>
                                <span dangerouslySetInnerHTML={{ __html: applyInline(it) }} />
                            </li>
                        ))}
                    </ul>
                )}
                {rule && (
                    <div className="rounded-xl bg-red-500/12 border border-red-500/30 p-3">
                        <p
                            className="text-sm text-white font-bold leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: applyInline(rule) }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

function KeyPhraseSlide({ phrase }) {
    return (
        <div className="max-w-2xl mx-auto text-center">
            <QuoteIcon className="w-10 h-10 text-shariti-orange-light mx-auto mb-5 opacity-60" />
            <motion.p
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight leading-tight"
                dangerouslySetInnerHTML={{ __html: applyInline(phrase) }}
            />
        </div>
    );
}

/**
 * Iter-30.32 — Slide de type "quote" : citation attribuée à un auteur.
 * Distincte de KeyPhraseSlide (= punchline pédagogique) car ici l'auteur
 * compte : on l'affiche en bas avec un dash em pour signifier l'attribution.
 */
function QuoteSlide({ body, author }) {
    return (
        <div className="max-w-2xl mx-auto text-center">
            <QuoteIcon className="w-9 h-9 text-shariti-orange-light/80 mx-auto mb-4" />
            <motion.p
                initial={{ y: 8, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="text-xl sm:text-2xl font-light italic text-white/95 leading-snug"
                dangerouslySetInnerHTML={{ __html: `« ${applyInline(body || "")} »` }}
            />
            {author && (
                <p className="mt-4 text-xs sm:text-sm font-extrabold tracking-wider uppercase text-shariti-orange-light">
                    — {author}
                </p>
            )}
        </div>
    );
}

function ListSlide({ title, items }) {
    return (
        <div className="max-w-3xl mx-auto">
            {title && (
                <h3 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight mb-5">
                    {title}
                </h3>
            )}
            <ul className="space-y-3">
                {(items || []).map((it, i) => (
                    <li
                        key={i}
                        className="flex items-start gap-3 text-base sm:text-lg text-white/85 font-light leading-relaxed"
                    >
                        <div className="w-6 h-6 rounded-full bg-shariti-orange/20 flex items-center justify-center shrink-0 mt-0.5 text-shariti-orange-light text-xs font-extrabold">
                            {i + 1}
                        </div>
                        <span dangerouslySetInnerHTML={{ __html: applyInline(it) }} />
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * Iter-30.32 — Slide de type "checklist" : liste d'auto-évaluation type
 * "Suis-je en posture XYZ ?". Visuellement proche d'une ListSlide mais
 * avec des icônes en cases à cocher (CheckCircle2) plutôt que des numéros,
 * pour communiquer une intention "à valider" plutôt que "à mémoriser".
 *
 * Cocher est purement local (pas persisté) — c'est un nudge visuel pour
 * que l'apprenant fasse l'introspection en lisant.
 */
function ChecklistSlide({ title, items }) {
    const [checked, setChecked] = useState(() => new Set());
    const toggle = (i) => {
        setChecked((prev) => {
            const n = new Set(prev);
            n.has(i) ? n.delete(i) : n.add(i);
            return n;
        });
    };
    return (
        <div className="max-w-3xl mx-auto">
            {title && (
                <h3 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight mb-5 text-center sm:text-left">
                    {title}
                </h3>
            )}
            <ul className="space-y-2.5" data-testid="checklist-slide-items">
                {(items || []).map((it, i) => {
                    const isOn = checked.has(i);
                    return (
                        <li key={i}>
                            <button
                                type="button"
                                onClick={() => toggle(i)}
                                data-testid={`checklist-slide-item-${i}`}
                                className={`w-full flex items-start gap-3 text-left text-[15px] sm:text-base text-white/90 font-light leading-relaxed px-3 py-2.5 rounded-2xl border transition-all ${
                                    isOn
                                        ? "bg-emerald-500/12 border-emerald-400/30"
                                        : "bg-white/3 border-white/10 hover:bg-white/5 hover:border-white/20"
                                }`}
                            >
                                <div
                                    className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 mt-0.5 transition-all ${
                                        isOn
                                            ? "bg-emerald-400/25 border border-emerald-400/40"
                                            : "bg-shariti-orange/12 border border-shariti-orange/30"
                                    }`}
                                >
                                    {isOn ? (
                                        <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                                    ) : (
                                        <span className="w-3.5 h-3.5 rounded border border-shariti-orange/50" />
                                    )}
                                </div>
                                <span
                                    className={isOn ? "text-white" : "text-white/85"}
                                    dangerouslySetInnerHTML={{ __html: applyInline(it) }}
                                />
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function GiftLevelsSlide({ title, fallback_intro }) {
    return (
        <div className="max-w-3xl mx-auto text-center">
            <div className="w-14 h-14 rounded-3xl bg-shariti-orange/15 flex items-center justify-center mx-auto mb-4">
                <Target className="w-7 h-7 text-shariti-orange-light" />
            </div>
            {title && (
                <h3 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight mb-3">
                    {title}
                </h3>
            )}
            <p
                className="text-base text-white/75 font-light leading-relaxed"
                dangerouslySetInnerHTML={{ __html: applyInline(fallback_intro || "Les paliers de don sont configurés par tes admins sur chaque association — ils s'adaptent automatiquement à ton affectation.") }}
            />
        </div>
    );
}

// ─── Map kind → renderer ─────────────────────────────────────────────────────
function renderSlideBody(slide, totalSteps) {
    switch (slide.kind) {
        case "intro":
            return <IntroSlide body={slide.body} />;
        case "punchline":
            return <PunchlineSlide body={slide.body} />;
        case "text":
            return <TextSlide title={slide.title} body={slide.body} />;
        case "stat_block":
            return <StatSlide label={slide.label} value={slide.value} context={slide.context} />;
        case "step":
            return (
                <StepSlide
                    number={slide.number}
                    title={slide.title}
                    body={slide.body}
                    example={slide.example}
                    total={totalSteps}
                />
            );
        case "do_dont":
            return <DoDontSlide doItems={slide.do || []} dontItems={slide.dont || []} />;
        case "warning_block":
            return (
                <WarningSlide
                    title={slide.title}
                    body={slide.body}
                    items={slide.items || []}
                    rule={slide.rule}
                />
            );
        case "key_phrase":
            return <KeyPhraseSlide phrase={slide.phrase} />;
        case "quote":
            return <QuoteSlide body={slide.body} author={slide.author} />;
        case "list":
            return <ListSlide title={slide.title} items={slide.items || []} />;
        case "checklist":
            return <ChecklistSlide title={slide.title} items={slide.items || []} />;
        case "gift_levels_dynamic":
            return <GiftLevelsSlide title={slide.title} fallback_intro={slide.fallback_intro} />;
        default:
            // Iter-30.32 — Hardening : JAMAIS de JSON.stringify visible côté
            // user. Si on tombe sur un kind inconnu, on essaie d'afficher le
            // titre + body s'ils existent, sinon un placeholder discret. Le
            // bug initial était un slide kind="checklist" non géré qui
            // affichait le JSON brut "{kind:checklist,...}" en plein écran.
            return (
                <TextSlide
                    title={slide.title}
                    body={
                        slide.body ||
                        (Array.isArray(slide.items) ? slide.items.join(" • ") : "")
                    }
                />
            );
    }
}

// ─── Extract the audio text from a slide (for TTS) ───────────────────────────
function extractSlideText(slide) {
    if (!slide) return "";
    const parts = [];
    if (slide.title) parts.push(humanizeForSpeech(slide.title));
    if (slide.body) parts.push(humanizeForSpeech(slide.body));
    if (slide.phrase) parts.push(humanizeForSpeech(slide.phrase));
    if (slide.label) parts.push(humanizeForSpeech(slide.label));
    if (slide.value) parts.push(`${humanizeForSpeech(String(slide.value))}.`);
    if (slide.context) parts.push(humanizeForSpeech(slide.context));
    if (slide.example) parts.push(`Exemple. ${humanizeForSpeech(slide.example)}`);
    // Iter-27.80 — Pour les listes numérotées (ListSlide affiche 1,2,3,… à
    // l'écran), on préfixe chaque item de son ordinal français pour que le
    // formateur le prononce vraiment.
    if (slide.items && Array.isArray(slide.items)) {
        if (slide.kind === "list") {
            const numbered = slide.items.map(
                (it, i) => `${capitalize(frenchOrdinal(i + 1))}, ${humanizeForSpeech(it)}.`,
            );
            parts.push(numbered.join(" "));
        } else if (slide.kind === "checklist") {
            // Iter-30.32 — On préfixe d'une intro "Vérifie pour toi" pour que
            // l'apprenant comprenne à l'oreille que c'est une auto-évaluation.
            parts.push(
                "Vérifie pour toi. " +
                    slide.items.map((it) => humanizeForSpeech(it)).join(". "),
            );
        } else {
            parts.push(slide.items.map(humanizeForSpeech).join(". "));
        }
    }
    if (slide.do && Array.isArray(slide.do))
        parts.push("À faire. " + slide.do.map(humanizeForSpeech).join(". "));
    if (slide.dont && Array.isArray(slide.dont))
        parts.push("À éviter. " + slide.dont.map(humanizeForSpeech).join(". "));
    if (slide.rule) parts.push(humanizeForSpeech(slide.rule));
    // Iter-30.32 — Citation : on lit l'attribution à voix haute aussi
    if (slide.kind === "quote" && slide.author) {
        parts.push(humanizeForSpeech(slide.author));
    }
    return parts.join(". ").replace(/\.{2,}/g, ".");
}

function capitalize(s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function LessonPresentation({
    rich_content,
    lessonId,
    onAllSlidesViewed,
    onAdvanceLesson, // Iter-27.39 — appelé à la dernière slide quand viewed
    minSecondsPerSlide = 3,
    // Iter-27.94 — Contexte du module pour ajuster le libellé du bouton final
    // ("Passer au quiz" au lieu de "Leçon suivante" sur la dernière leçon
    // quand un quiz est dispo). Utile pour rendre la transition vers le quiz
    // explicite et non confuse pour l'user.
    isLastLessonOfModule = false,
    hasQuiz = false,
}) {
    const slides = useMemo(() => buildSlides(rich_content), [rich_content]);
    const totalSteps = useMemo(
        () => slides.filter((s) => s.kind === "step").length,
        [slides],
    );
    const [idx, setIdx] = useState(0);
    const [viewed, setViewed] = useState(new Set([0]));
    const [audioState, setAudioState] = useState("idle"); // idle | loading | playing
    // Iter-27.45 — Mode autoplay : quand l'audio se termine, on avance automatiquement
    // à la slide suivante et on relance l'audio. À la dernière slide, on déclenche
    // onAdvanceLesson (passage à la leçon suivante). Activé par l'utilisateur via
    // le bouton "Écouter" — désactivé sur Pause manuelle.
    const [autoplay, setAutoplay] = useState(false);
    const autoplayRef = useRef(false);
    useEffect(() => { autoplayRef.current = autoplay; }, [autoplay]);
    const audioRef = useRef(null);
    // Iter-27.47 — Token générationnel anti-race-condition + audioManager handle
    const playbackTokenRef = useRef(0);
    const audioHandleRef = useRef(null);
    if (!audioHandleRef.current) {
        audioHandleRef.current = audioManager.acquire("lesson-presentation");
    }

    // Iter-27.47 — Cleanup global au démontage (navigation, logout) ET au
    // changement de leçon (lessonId change → on stoppe tout audio en cours).
    useEffect(() => {
        return () => {
            playbackTokenRef.current += 1; // invalide tous les onended pending
            if (audioRef.current) {
                try { audioRef.current.pause(); audioRef.current.src = ""; } catch { /* */ }
                audioRef.current = null;
            }
            if (audioHandleRef.current) audioHandleRef.current.release();
            setAudioState("idle");
            setAutoplay(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lessonId]);

    // Iter-27.47 — Réagir aux notifications de l'audioManager : si une autre
    // source (ex: podcast) prend la main, on bascule notre UI en idle.
    useEffect(() => {
        const unsub = audioManager.subscribe((evt) => {
            if (evt.type === "stopped-by-other" && evt.ownerId === "lesson-presentation") {
                playbackTokenRef.current += 1;
                if (audioRef.current) {
                    try { audioRef.current.pause(); } catch { /* */ }
                    audioRef.current = null;
                }
                setAudioState("idle");
                setAutoplay(false);
            }
        });
        return unsub;
    }, []);

    // ⏱ Auto-mark current slide as "viewed" after minSecondsPerSlide
    useEffect(() => {
        if (slides.length === 0) return;
        const t = setTimeout(() => {
            setViewed((prev) => {
                if (prev.has(idx)) return prev;
                const next = new Set(prev);
                next.add(idx);
                return next;
            });
        }, minSecondsPerSlide * 1000);
        return () => clearTimeout(t);
    }, [idx, slides.length, minSecondsPerSlide]);

    // Stop audio when slide changes
    // Iter-27.47 — Stop audio when slide changes (manual nav OR autoplay advance).
    // Token bump → tous les onended/onpause des audios précédents deviennent no-op.
    useEffect(() => {
        playbackTokenRef.current += 1;
        if (audioRef.current) {
            try { audioRef.current.pause(); audioRef.current.src = ""; } catch { /* */ }
            audioRef.current = null;
        }
        setAudioState("idle");
    }, [idx]);

    // Notify parent when all slides viewed
    useEffect(() => {
        if (slides.length > 0 && viewed.size >= slides.length && onAllSlidesViewed) {
            onAllSlidesViewed();
        }
    }, [viewed, slides.length, onAllSlidesViewed]);

    // Keyboard nav
    useEffect(() => {
        const handler = (e) => {
            if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
            if (e.key === "ArrowRight") goNext();
            else if (e.key === "ArrowLeft") goPrev();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx, slides.length]);

    const goPrev = useCallback(() => {
        setIdx((i) => Math.max(0, i - 1));
    }, []);
    const goNext = useCallback(() => {
        setIdx((i) => Math.min(slides.length - 1, i + 1));
    }, [slides.length]);

    // Iter-27.45 / 27.47 / 27.48 — Lit l'audio de la slide courante (TTS).
    // - Token générationnel : ignore les onended/onplay des audios obsolètes
    // - audioManager : pause toute autre source audio (podcast, etc.)
    // - 27.48 : pause/resume PRÉSERVE la position de lecture (currentTime gardée)
    // - Si `enableAutoplay` est true → mode lecture continue jusqu'à fin du module
    const playAudio = useCallback(async (enableAutoplay = false) => {
        // Iter-27.48 — Toggle pause : on garde l'audio en mémoire pour resume.
        if (audioState === "playing" && audioRef.current) {
            try { audioRef.current.pause(); } catch { /* */ }
            setAudioState("idle");
            // NB: on ne null PAS audioRef et on ne bump PAS le token → resume préservera currentTime.
            return;
        }
        // Iter-27.48 — Resume sans refetch si même slide ET audio en pause valide
        if (
            audioRef.current
            && audioRef.current.paused
            && audioRef.current.currentTime > 0
            && audioRef.current.currentTime < audioRef.current.duration - 0.1
        ) {
            if (enableAutoplay) setAutoplay(true);
            audioHandleRef.current.attach(audioRef.current);
            try {
                await audioRef.current.play();
                setAudioState("playing");
            } catch {
                setAudioState("idle");
            }
            return;
        }
        if (enableAutoplay) setAutoplay(true);
        const text = extractSlideText(slides[idx]);
        if (!text.trim()) {
            if (autoplayRef.current) {
                if (idx < slides.length - 1) setIdx(idx + 1);
                else if (onAdvanceLesson && viewed.has(idx)) onAdvanceLesson();
            }
            return;
        }

        // Iter-27.47 — nouveau token (fresh play depuis le début)
        playbackTokenRef.current += 1;
        const myToken = playbackTokenRef.current;
        const currentLessonId = lessonId;
        const currentIdx = idx;

        if (audioRef.current) {
            try { audioRef.current.pause(); audioRef.current.src = ""; } catch { /* */ }
            audioRef.current = null;
        }
        setAudioState("loading");

        try {
            const res = await fetch(
                `${API_BASE}/lessons/${encodeURIComponent(currentLessonId)}/slide/${currentIdx}/audio`,
                { credentials: "include" },
            );
            if (!res.ok) throw new Error("audio failed");
            if (myToken !== playbackTokenRef.current) return;
            const blob = await res.blob();
            if (myToken !== playbackTokenRef.current) return;

            const audio = new Audio(URL.createObjectURL(blob));
            audio.onended = () => {
                if (myToken !== playbackTokenRef.current) return;
                setAudioState("idle");
                if (!autoplayRef.current) return;
                if (currentIdx < slides.length - 1) {
                    setIdx(currentIdx + 1);
                } else if (onAdvanceLesson && viewed.has(currentIdx)) {
                    onAdvanceLesson();
                }
            };
            audio.onpause = () => {
                if (myToken !== playbackTokenRef.current) return;
                if (audio.currentTime >= audio.duration - 0.1) setAudioState("idle");
            };

            audioHandleRef.current.attach(audio);
            audioRef.current = audio;
            if (myToken !== playbackTokenRef.current) {
                try { audio.pause(); audio.src = ""; } catch { /* */ }
                return;
            }
            await audio.play();
            if (myToken !== playbackTokenRef.current) {
                try { audio.pause(); audio.src = ""; } catch { /* */ }
                return;
            }
            setAudioState("playing");
        } catch {
            if (myToken === playbackTokenRef.current) {
                setAudioState("idle");
                setAutoplay(false);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx, slides, audioState, lessonId, onAdvanceLesson, viewed]);

    // Iter-27.45 — Quand on change de slide ET que autoplay est actif → relancer la lecture.
    useEffect(() => {
        if (autoplayRef.current && slides.length > 0) {
            // Petit délai pour laisser la slide s'afficher avant la lecture
            const t = setTimeout(() => playAudio(false), 300);
            return () => clearTimeout(t);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx]);

    if (slides.length === 0) {
        return (
            <div className="text-center py-16 text-white/40">
                Aucune slide à afficher pour cette leçon.
            </div>
        );
    }

    const current = slides[idx];
    const progressPercent = ((viewed.size) / slides.length) * 100;

    return (
        <div className="relative" data-testid="lesson-presentation">
            {/* Progress bar segmentée */}
            <div className="flex gap-1 mb-5">
                {slides.map((_, i) => (
                    <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-all ${
                            i === idx
                                ? "bg-shariti-orange"
                                : viewed.has(i)
                                  ? "bg-shariti-orange/55"
                                  : "bg-white/8"
                        }`}
                    />
                ))}
            </div>

            {/* Top meta */}
            <div className="flex items-center justify-between mb-4 text-[11px]">
                <span className="font-thin tracking-[0.25em] uppercase text-shariti-orange-light">
                    Slide {idx + 1} / {slides.length}
                </span>
                <span className="font-thin tracking-[0.25em] uppercase text-white/40">
                    {viewed.size}/{slides.length} vues
                </span>
            </div>

            {/* Slide */}
            <div
                className="glass-card rounded-3xl p-6 sm:p-8 lg:p-10 min-h-[55vh] flex items-center justify-center overflow-hidden"
                onClick={(e) => {
                    // Tap right side = next, left side = prev (mobile-friendly)
                    if (e.target.closest("button, a, input, textarea")) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    if (e.clientX - rect.left > rect.width / 2) goNext();
                    else goPrev();
                }}
            >
                <AnimatePresence mode="wait">
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: 24 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -24 }}
                        transition={{ duration: 0.28 }}
                        className="w-full"
                        data-testid={`slide-${idx}`}
                    >
                        {renderSlideBody(current, totalSteps)}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Bottom controls */}
            <div className="flex items-center justify-between mt-5 gap-3">
                <button
                    onClick={goPrev}
                    disabled={idx === 0}
                    data-testid="slide-prev"
                    className="inline-flex items-center gap-1.5 py-2.5 px-4 rounded-2xl bg-white/5 text-white/75 hover:bg-white/10 hover:text-white text-sm font-bold disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                    <ChevronLeft className="w-4 h-4" />
                    Précédent
                </button>

                <button
                    onClick={() => playAudio(true)}
                    disabled={audioState === "loading"}
                    data-testid="slide-audio-btn"
                    className={`inline-flex items-center gap-1.5 py-2 px-3.5 rounded-full text-xs font-bold transition-all disabled:opacity-50 border ${
                        autoplay
                            ? "bg-shariti-orange text-white border-shariti-orange orange-glow"
                            : "bg-shariti-orange/12 border-shariti-orange/30 text-shariti-orange-light hover:bg-shariti-orange/20"
                    }`}
                    title={autoplay ? "Lecture continue active · clique pour pauser" : "Écouter + défilement automatique des slides"}
                >
                    {audioState === "loading" ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : audioState === "playing" ? (
                        <Pause className="w-3.5 h-3.5" />
                    ) : (
                        <Volume2 className="w-3.5 h-3.5" />
                    )}
                    {autoplay && audioState !== "loading" ? "Auto" : "Écouter"}
                </button>

                <button
                    onClick={() => {
                        // Iter-27.39 — Si on est à la dernière slide ET qu'on l'a vue assez longtemps,
                        // le bouton "Suivant" complète la leçon et passe à la leçon suivante (logique parent).
                        if (idx === slides.length - 1 && viewed.has(idx) && onAdvanceLesson) {
                            onAdvanceLesson();
                            return;
                        }
                        goNext();
                    }}
                    disabled={
                        idx === slides.length - 1 &&
                        (!onAdvanceLesson || !viewed.has(idx))
                    }
                    data-testid="slide-next"
                    className={`inline-flex items-center gap-1.5 py-2.5 px-4 rounded-2xl text-sm font-bold transition-all disabled:opacity-30 disabled:cursor-not-allowed ${
                        viewed.has(idx)
                            ? "bg-shariti-orange text-white orange-glow hover:bg-shariti-orange-light"
                            : "bg-white/5 text-white/75 hover:bg-white/10 hover:text-white"
                    }`}
                >
                    {idx === slides.length - 1 && onAdvanceLesson
                        ? viewed.has(idx)
                            ? isLastLessonOfModule && hasQuiz
                                ? "Passer au quiz"
                                : isLastLessonOfModule
                                  ? "Terminer le module"
                                  : "Leçon suivante"
                            : "Patiente quelques secondes…"
                        : "Suivant"}
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>

            {/* Subtle hint mobile */}
            <p className="text-[10px] text-white/35 font-light text-center mt-3 hidden sm:block">
                Astuce : flèches ← → du clavier · clic gauche/droit sur la slide · "Écouter" active la lecture continue (idéal en transports)
            </p>
        </div>
    );
}
